import React, { useState, useEffect, useCallback } from 'react';
import { momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import BookingModal from './BookingModal';
import SinglePaymentModal from './SinglePaymentModal';
import useSchedule from './useSchedule';
import CalendarComponent from './CalendarComponent';
import ScheduleTable from './ScheduleTable';
import { fetchWithAuth } from '../utils/apiHelper';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

moment.locale('es');
const localizer = momentLocalizer(moment);

function MyCalendar() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState({ employee: null, date: null, time: null });
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [hasExceso, setHasExceso] = useState(false); // Track excess state

  // Function to fetch excess cash status
  const fetchCajaExceso = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/caja/exceso');

      if (data.superado) {
        setHasExceso(true);
        toast.error(`⚠️ Exceso en caja: $${data.exceso.toLocaleString()}. Realizar retiro urgente!`, {
          position: "top-right",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
        });
      } else {
        setHasExceso(false); // Reset the state if excess is resolved
      }
    } catch (error) {
      console.error('Error fetching caja exceso:', error);
    }
  }, []);

  useEffect(() => {
    fetchCajaExceso(); // Initial check on mount

    const interval = setInterval(() => {
      fetchCajaExceso();
    }, 2 * 60 * 1000); // Repeat every 2 minutes

    return () => clearInterval(interval); // Cleanup on unmount
  }, [fetchCajaExceso]);

  const {
    employees,
    appointments,
    loading,
    sucursalData,
    fetchEmployeesAndSchedules,
    fetchAppointments,
    clearAppointments,
    fetchHorariosLocal
  } = useSchedule(selectedDate);

  useEffect(() => {
    fetchEmployeesAndSchedules();
    fetchAppointments(selectedDate);
    fetchHorariosLocal();
  }, [selectedDate, fetchEmployeesAndSchedules, fetchAppointments]);

  const handleNewBooking = () => {
    fetchAppointments(selectedDate);
  };

  const openModal = useCallback((employee, date, time) => {
    setModalData({ employee, date, time });
    setIsModalOpen(true);
  }, []);

  const handleBookingClick = (employee, time) => {
    openModal(employee, moment(selectedDate).format('YYYY-MM-DD'), time);
  };

  const handleSelectSlot = ({ start }) => {
    setSelectedDate(start);
    clearAppointments();
    fetchAppointments(start);
  };

  const dayPropGetter = useCallback((date) => {
    const isSelectedDate = moment(date).isSame(selectedDate, 'day');
    return {
      style: {
        backgroundColor: isSelectedDate ? '#ffc107' : '',
        color: isSelectedDate ? 'white' : 'black'
      }
    };
  }, [selectedDate]);

  return (
    <div className={`${loading ? 'cursor-progress' : 'cursor-default'} w-full mx-auto px-4 sm:px-6 lg:px-8 py-6`}>
      <CalendarComponent
        localizer={localizer}
        onSelectSlot={handleSelectSlot}
        dayPropGetter={dayPropGetter}
        selectedDate={selectedDate}
      />

      <div className="mt-4 bg-salon-blue text-center text-xl font-semibold p-4 rounded-lg shadow-lg border-2 border-salon-pink">
        <span className="text-salon-dark-purple">
          Turnos del día {moment(selectedDate).format('dddd D [del mes de] MMMM [del año] YYYY')}
        </span>
      </div>

      <button
        className="mt-4 bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg transition-transform transform hover:scale-105"
        onClick={() => setIsPaymentModalOpen(true)}
      >
        Crear Pago
      </button>





      {/* Status Colors Legend */}
      <div className="mt-4 p-4 bg-white rounded-lg shadow-md border border-gray-300">
        <h3 className="text-lg font-semibold text-gray-700">Estados de Turnos</h3>
        <div className="grid grid-cols-2 gap-2 mt-2">
          
          {/* Reservado */}
          <div className="flex items-center p-2 rounded-lg border-green-500 bg-green-200">
            <span className="w-4 h-4 rounded-full bg-green-500 mr-2"></span>
            <span className="text-gray-700 font-medium">Reservado - Turno aún no confirmado.</span>
          </div>

          {/* Confirmado */}
          <div className="flex items-center p-2 rounded-lg border-blue-500 bg-blue-200">
            <span className="w-4 h-4 rounded-full bg-blue-500 mr-2"></span>
            <span className="text-gray-700 font-medium">Confirmado - Cliente confirmó asistencia.</span>
          </div>

          {/* Senado */}
          <div className="flex items-center p-2 rounded-lg border-purple-500 bg-purple-200">
            <span className="w-4 h-4 rounded-full bg-purple-500 mr-2"></span>
            <span className="text-gray-700 font-medium">Senado - Cliente pagó una seña.</span>
          </div>

          {/* Pagado */}
          <div className="flex items-center p-2 rounded-lg border-yellow-500 bg-yellow-200">
            <span className="w-4 h-4 rounded-full bg-yellow-500 mr-2"></span>
            <span className="text-gray-700 font-medium">Pagado - Turno pagado en su totalidad.</span>
          </div>

        </div>
      </div>






      <ScheduleTable
        sucursal={sucursalData}
        appointments={appointments}
        employees={employees}
        dia={moment(selectedDate).format('dddd')}
        onBookingClick={handleBookingClick}
        updateAppointments={handleNewBooking}
      />

      {isModalOpen && (
        <BookingModal
          isOpen={isModalOpen}
          closeModal={() => setIsModalOpen(false)}
          data={modalData}
          updateAppointments={handleNewBooking}
        />
      )}

      {isPaymentModalOpen && (
        <SinglePaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          loadingState={false}
          updateAppointments={handleNewBooking}
        />
      )}
    </div>
  );
}

export default MyCalendar;
